<script setup lang="ts">
/**
 * SidebarNav — de documentatieboom in de linkerbalk.
 *
 * Categorieën → subcategorieën → pagina's, met per item een ⋯-menu
 * (hernoemen, toevoegen, zichtbaarheid, omhoog/omlaag, verplaats naar,
 * verwijderen). Beheer-affordances verschijnen alleen wanneer bewerken
 * is toegestaan (ingelogd en niet in voorbeeldweergave).
 *
 * Toegankelijkheid: alle acties zijn bereikbaar via toetsenbord (de
 * menuknoppen zijn echte buttons met aria-labels; Escape sluit het menu,
 * focus keert terug naar de menuknop). Drag & drop komt later als extra
 * bovenop deze route, nooit in plaats ervan.
 */

import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useSectionStore } from '../../stores/sections'
import { useUiStore } from '../../stores/ui'
import { useNotificationStore } from '../../stores/notifications'
import { useDragReorder } from '../../composables/useDragReorder'
import type { Section, Page } from '../../types'
import {
  ChevronRight,
  Folder,
  FileText,
  Plus,
  MoreHorizontal,
  Pencil,
  Copy,
  Eye,
  EyeOff,
  Clock,
  ArrowUp,
  ArrowDown,
  FolderInput,
  Lock,
  Trash2,
} from 'lucide-vue-next'
import CategoryAccessDialog from '../access/CategoryAccessDialog.vue'

const props = withDefaults(defineProps<{
  /** Alleen de actieve hoofdcategorie tonen (desktop-sidebar).
   *  De mobiele drawer toont de volledige boom, want de
   *  hoofdcategorieën in de header zijn daar niet zichtbaar. */
  scoped?: boolean
}>(), { scoped: false })

const route = useRoute()
const router = useRouter()
const store = useSectionStore()
const ui = useUiStore()
const notifications = useNotificationStore()
const { t } = useI18n()

/** Weer te geven wortels: gescoped = alleen de actieve hoofdcategorie.
 *  Presentatiefilter — store.tree zelf blijft de volledige boom
 *  (PagesView, "Verplaats naar"-doelen en Omhoog/Omlaag hebben die nodig). */
const visibleTree = computed<Section[]>(() =>
  props.scoped && store.activeRoot ? [store.activeRoot] : store.tree
)

/* ── Uitklap-status ─────────────────────────────────────────── */
const expanded = ref<Set<number>>(new Set())

function toggleExpanded(id: number) {
  if (expanded.value.has(id)) expanded.value.delete(id)
  else expanded.value.add(id)
  expanded.value = new Set(expanded.value)
}

// Eerste keer: alles op hoofdniveau uitgeklapt
watch(
  () => store.tree.length,
  (len) => {
    if (len > 0 && expanded.value.size === 0) {
      expanded.value = new Set(store.tree.map(s => s.id))
    }
  },
  { immediate: true }
)

// De actieve hoofdcategorie is altijd opengeklapt (header-klik toont
// direct de inhoud in het gescopede menu).
watch(
  () => store.activeRoot?.id,
  (id) => {
    if (id != null && !expanded.value.has(id)) {
      expanded.value = new Set(expanded.value.add(id))
    }
  },
  { immediate: true }
)

/* ── Inline invoer (nieuw item / hernoemen) ─────────────────── */
type AddState = { parentId: number | null; kind: 'category' | 'page' } | null
type RenameState = { kind: 'section' | 'page'; id: number; current: string } | null

const adding = ref<AddState>(null)
const renaming = ref<RenameState>(null)
const inputValue = ref('')
const inputEl = ref<HTMLInputElement[]>([])

async function focusInput() {
  await nextTick()
  const el = inputEl.value[0]
  if (el) { el.focus(); el.select() }
}

function startAdd(parentId: number | null, kind: 'category' | 'page') {
  closeMenu()
  renaming.value = null
  adding.value = { parentId, kind }
  inputValue.value = ''
  if (parentId !== null) expanded.value = new Set(expanded.value.add(parentId))
  focusInput()
}

function startRename(kind: 'section' | 'page', id: number, current: string) {
  closeMenu()
  adding.value = null
  renaming.value = { kind, id, current }
  inputValue.value = current
  focusInput()
}

function cancelInput() {
  adding.value = null
  renaming.value = null
}

async function commitInput() {
  const value = inputValue.value.trim()
  const add = adding.value
  const rename = renaming.value
  cancelInput()

  if (!value) return

  try {
    if (add) {
      if (add.kind === 'category') {
        await store.createSection({ name: value, parentId: add.parentId })
      } else if (add.parentId !== null) {
        // Meteen de editor in: je hebt net een naam bedacht, dus je wilt aan
        // de slag. Zonder deze stap bleef je op de pagina staan die toevallig
        // openstond, en leek er niets te gebeuren.
        const pagina = await store.createPage({ title: value, sectionId: add.parentId })
        if (pagina?.id) router.push({ name: 'page-editor', params: { id: pagina.id } })
      }
    } else if (rename && value !== rename.current) {
      if (rename.kind === 'section') await store.updateSection(rename.id, { name: value })
      else await store.updatePage(rename.id, { title: value })
    }
  } catch (err: any) {
    notifications.error(err.response?.data?.message || t('nav.saveFailed'))
  }
}

/* ── Flyout-menu ────────────────────────────────────────────── */
type MenuTarget =
  | { kind: 'section'; section: Section; siblings: Section[]; parentId: number | null }
  // parentId is gezet wanneer de pagina een tab (kindpagina) is.
  | { kind: 'page'; page: Page; siblings: Page[]; sectionId: number; parentId?: number }

const menuTarget = ref<MenuTarget | null>(null)
const menuPos = ref({ top: 0, left: 0, maxHeight: 9999 })
const menuEl = ref<HTMLElement | null>(null)
const showMoveTargets = ref(false)
let menuAnchor: HTMLElement | null = null

/* De +-knop op een categorierij opent hetzelfde flyout, maar dan met
   alléén de toevoeg-acties ('add'). Voorheen opende + het volledige
   ⋯-menu - twee knoppen die hetzelfde deden, en wie iets wilde toevoegen
   moest dat tussen hernoemen/verplaatsen/verwijderen zoeken. */
const menuMode = ref<'full' | 'add'>('full')

const menuOpenForId = computed(() => {
  if (!menuTarget.value) return null
  return menuTarget.value.kind === 'section'
    ? `s-${menuTarget.value.section.id}`
    : `p-${menuTarget.value.page.id}`
})

function openMenu(event: MouseEvent, target: MenuTarget, mode: 'full' | 'add' = 'full') {
  const anchor = (event.currentTarget as HTMLElement)
  menuAnchor = anchor
  menuTarget.value = target
  menuMode.value = mode
  showMoveTargets.value = false

  // Voorlopige positie; de definitieve wordt na render bepaald op basis
  // van de werkelijke menu-hoogte (zie positionMenu).
  const rect = anchor.getBoundingClientRect()
  menuPos.value = { top: rect.bottom + 4, left: rect.left, maxHeight: window.innerHeight - 16 }

  nextTick(() => {
    positionMenu()
    menuEl.value?.querySelector('button')?.focus()
  })
}

/** Klap het "Verplaats naar"-submenu in/uit en herpositioneer, want de
 *  menu-hoogte verandert daardoor. */
function toggleMoveTargets() {
  showMoveTargets.value = !showMoveTargets.value
  nextTick(positionMenu)
}

/**
 * Klem het (position: fixed) menu binnen de viewport op basis van zijn
 * werkelijke afmetingen. Bij te weinig ruimte onder het anker klapt het
 * boven het anker; anders schuift het omhoog tot het past. Zo raakt het
 * nooit onbereikbaar buiten beeld (je kunt een fixed element niet scrollen).
 */
function positionMenu() {
  const el = menuEl.value
  const anchor = menuAnchor
  if (!el || !anchor) return

  const margin = 8
  const rect = anchor.getBoundingClientRect()
  const menuW = el.offsetWidth

  // Begrens de hoogte tot de viewport (min marges); bij overschrijding
  // scrollt het menu intern (overflow-y: auto).
  const available = window.innerHeight - margin * 2
  const maxHeight = available
  const menuH = Math.min(el.offsetHeight, available)

  // Verticaal: standaard onder het anker; klap boven als het niet past;
  // klem anders op de bovenmarge zodat het altijd volledig in beeld is.
  let top = rect.bottom + 4
  if (top + menuH > window.innerHeight - margin) {
    const aboveTop = rect.top - 4 - menuH
    top = aboveTop >= margin
      ? aboveTop
      : Math.max(margin, window.innerHeight - margin - menuH)
  }

  // Horizontaal: links uitlijnen, maar binnen beeld houden.
  let left = Math.min(rect.left, window.innerWidth - margin - menuW)
  left = Math.max(margin, left)

  menuPos.value = { top, left, maxHeight }
}

function closeMenu(returnFocus = false) {
  menuTarget.value = null
  showMoveTargets.value = false
  if (returnFocus && menuAnchor) menuAnchor.focus()
  menuAnchor = null
}

function onDocumentClick(event: MouseEvent) {
  if (menuEl.value && !menuEl.value.contains(event.target as Node)) closeMenu()
}
function onDocumentKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    if (menuTarget.value) closeMenu(true)
    else cancelInput()
  }
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick, true)
  document.addEventListener('keydown', onDocumentKeydown)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick, true)
  document.removeEventListener('keydown', onDocumentKeydown)
})

/* ── Menu-acties ────────────────────────────────────────────── */
const target = computed(() => menuTarget.value)

const targetName = computed(() => {
  if (!target.value) return ''
  return target.value.kind === 'section' ? target.value.section.name : target.value.page.title
})

const targetVisible = computed(() => {
  if (!target.value) return true
  return target.value.kind === 'section' ? target.value.section.isVisible : target.value.page.isVisible
})

const targetIndex = computed(() => {
  if (!target.value) return -1
  if (target.value.kind === 'section') {
    return target.value.siblings.findIndex(s => s.id === (target.value as any).section.id)
  }
  return target.value.siblings.findIndex(p => p.id === (target.value as any).page.id)
})

async function runAction(action: () => Promise<void>, errorMessage: string) {
  try {
    await action()
  } catch (err: any) {
    notifications.error(err.response?.data?.message || errorMessage)
  }
}

/* ── Slepen (herordenen binnen een siblinggroep) ────────────────
   Additief bovenop het ⋯-menu (omhoog/omlaag). Cross-categorie
   verplaatsen blijft via "Verplaats naar". */
const dnd = useDragReorder('y')

async function dropSections(items: Section[], parentId: number | null) {
  const reordered = dnd.apply(items)
  dnd.reset()
  if (!reordered) return
  await runAction(
    () => store.reorderSections(reordered.map((s, i) => ({ id: s.id, sortOrder: i, parentId }))),
    t('nav.moveFailed')
  )
}

async function dropPages(items: Page[], sectionId: number) {
  const reordered = dnd.apply(items)
  dnd.reset()
  if (!reordered) return
  await runAction(
    () => store.reorderPages(reordered.map((p, i) => ({ id: p.id, sortOrder: i, sectionId }))),
    t('nav.moveFailed')
  )
}

async function duplicatePage() {
  const tgt = target.value
  if (!tgt || tgt.kind !== 'page') return
  closeMenu(true)
  await runAction(async () => {
    await store.duplicatePage(tgt.page.id)
  }, t('nav.duplicateFailed'))
}

async function toggleVisibility() {
  const tgt = target.value
  if (!tgt) return
  closeMenu(true)
  await runAction(async () => {
    if (tgt.kind === 'section') await store.updateSection(tgt.section.id, { isVisible: !tgt.section.isVisible })
    else await store.updatePage(tgt.page.id, { isVisible: !tgt.page.isVisible })
  }, t('nav.visibilityFailed'))
}

/* ── Slot van een categorie ─────────────────────────────────── */
const accessDialogSection = ref<Section | null>(null)

function openAccessDialog() {
  const tgt = target.value
  if (!tgt || tgt.kind !== 'section') return
  closeMenu(true)
  accessDialogSection.value = tgt.section
}

/** Zet deze categorie zelf een slot (los van wat hij van de site erft)? */
function hasOwnLock(section: Section): boolean {
  return Boolean(section.accessMode && section.accessMode !== 'inherit')
}

async function moveBy(offset: number) {
  const tgt = target.value
  const index = targetIndex.value
  if (!tgt || index < 0) return
  const newIndex = index + offset
  if (newIndex < 0 || newIndex >= tgt.siblings.length) return
  closeMenu(true)

  const reordered = [...tgt.siblings]
  const [moved] = reordered.splice(index, 1)
  reordered.splice(newIndex, 0, moved)

  await runAction(async () => {
    if (tgt.kind === 'section') {
      await store.reorderSections(reordered.map((s, i) => ({ id: s.id, sortOrder: i, parentId: tgt.parentId })))
    } else {
      await store.reorderPages(reordered.map((p, i) => ({ id: p.id, sortOrder: i, sectionId: tgt.sectionId })))
    }
  }, t('nav.moveFailed'))
}

/** Doellocaties voor "verplaats naar". */
const moveTargets = computed(() => {
  const tgt = target.value
  if (!tgt) return []
  const targets: Array<{ id: number | null; name: string; depth: number }> = []

  if (tgt.kind === 'section') {
    const section = tgt.section
    // Subcategorie kan terug naar hoofdniveau
    if (section.parentId !== null) targets.push({ id: null, name: t('nav.topLevel'), depth: 0 })
    for (const { section: candidate, depth } of store.allCategories()) {
      if (candidate.id === section.id) continue
      if (depth > 0) continue // max diepte: alleen top-categorieën als doel
      if (candidate.id === section.parentId) continue // huidige locatie
      // Categorie met subcategorieën kan zelf geen subcategorie worden
      if (section.parentId === null && (section.children?.length ?? 0) > 0) continue
      targets.push({ id: candidate.id, name: candidate.name, depth })
    }
  } else {
    for (const { section: candidate, depth } of store.allCategories()) {
      if (candidate.id === tgt.sectionId) continue // huidige locatie
      targets.push({ id: candidate.id, name: candidate.name, depth })
    }
  }

  return targets
})

async function moveTo(targetId: number | null) {
  const tgt = target.value
  if (!tgt) return
  closeMenu(true)
  await runAction(async () => {
    if (tgt.kind === 'section') {
      await store.updateSection(tgt.section.id, { parentId: targetId })
    } else if (targetId !== null) {
      const destination = store.findSection(targetId)
      await store.updatePage(tgt.page.id, {
        sectionId: targetId,
        sortOrder: destination?.pages?.length ?? 0,
      })
      expanded.value = new Set(expanded.value.add(targetId))
    }
  }, t('nav.moveFailed'))
}

async function confirmDelete() {
  const tgt = target.value
  if (!tgt) return

  const name = tgt.kind === 'section' ? tgt.section.name : tgt.page.title
  const tabCount = tgt.kind === 'page' ? (tgt.page.children?.length ?? 0) : 0
  const message = tgt.kind === 'section'
    ? t('nav.confirmDeleteCategory', { name })
    : tabCount > 0
      ? t('nav.confirmDeletePageWithTabs', { name, count: tabCount })
      : t('nav.confirmDeletePage', { name })

  closeMenu(true)
  if (!confirm(message)) return

  await runAction(async () => {
    if (tgt.kind === 'section') await store.deleteSection(tgt.section.id)
    else await store.deletePage(tgt.page.id)
  }, t('nav.deleteFailed'))
}

/* ── Weergave-helpers ───────────────────────────────────────── */
// Met parent (tab): bezoekers krijgen de 3-segments tab-URL.
function pageLink(page: Page, section: Section, parent?: Page): string {
  if (ui.canEdit) return `/pages/${page.id}`
  return parent
    ? `/docs/${section.slug}/${parent.slug}/${page.slug}`
    : `/docs/${section.slug}/${page.slug}`
}

function isActivePage(page: Page, section: Section, parent?: Page): boolean {
  if (route.path === `/pages/${page.id}`) return true
  return parent
    ? route.path === `/docs/${section.slug}/${parent.slug}/${page.slug}`
    : route.path === `/docs/${section.slug}/${page.slug}`
}

/** Tabs kunnen niet naar een andere categorie verplaatst worden. */
const isTabTarget = computed(() =>
  target.value?.kind === 'page' && target.value.parentId !== undefined
)
</script>

<template>
  <div>
    <!-- Lege staat: zonder dit toont een vers account hier stilte, en is
         "waar begin ik?" aan de gebruiker om te raden. De editor krijgt de
         verwijzing naar de knop die het oplost; de bezoeker een neutraal
         bericht. -->
    <p
      v-if="!store.isLoading && visibleTree.length === 0"
      class="px-2 py-1 text-sm text-gray-500"
    >
      {{ ui.canEdit ? t('nav.emptyEditor') : t('nav.emptyVisitor') }}
    </p>

    <ul role="list" class="nav-tree">
      <template v-for="(category, catIndex) in visibleTree" :key="category.id">
        <li>
          <!-- Categorie-rij. Root-DnD alleen ongescoped (drawer): met één
               zichtbare wortel is herordenen zinloos en kloppen indices niet. -->
          <div
            class="nav-row group"
            :class="{
              'opacity-60': !category.isVisible && ui.canEdit,
              'menu-open': menuOpenForId === `s-${category.id}`,
              'dragging': dnd.isDragged('root', catIndex),
              'drop-before': dnd.lineBefore('root', catIndex),
              'drop-after': dnd.lineAfterLast('root', catIndex, store.tree.length),
            }"
            :draggable="ui.canEdit && !scoped ? 'true' : undefined"
            @dragstart="dnd.start($event, 'root', catIndex)"
            @dragover="dnd.over($event, 'root', catIndex)"
            @drop.prevent="dropSections(store.tree, null)"
            @dragend="dnd.reset()"
          >
            <button
              type="button"
              class="row-main"
              :aria-expanded="expanded.has(category.id)"
              @click="toggleExpanded(category.id)"
            >
              <ChevronRight
                class="w-3.5 h-3.5 text-gray-400 transition-transform flex-shrink-0"
                :class="{ 'rotate-90': expanded.has(category.id) }"
                aria-hidden="true"
              />
              <Folder class="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
              <input
                v-if="renaming?.kind === 'section' && renaming.id === category.id"
                ref="inputEl"
                v-model="inputValue"
                class="inline-input"
                :aria-label="t('nav.newNameLabel')"
                @keydown.enter.prevent="commitInput"
                @blur="commitInput"
                @click.stop
              >
              <span v-else class="row-name font-semibold">{{ category.name }}</span>
              <EyeOff v-if="!category.isVisible && ui.canEdit" class="w-3.5 h-3.5 text-amber-600 flex-shrink-0" aria-hidden="true" />
              <Lock
                v-if="ui.canEdit ? hasOwnLock(category) : category.isLocked"
                class="w-3.5 h-3.5 text-gray-400 flex-shrink-0"
                :aria-label="t('access.category.lockedLabel')"
              />
            </button>
            <span v-if="ui.canEdit" class="row-actions">
              <button type="button" class="row-icon-btn row-icon-btn--add" :aria-label="t('nav.addToLabel', { name: category.name })" :title="t('nav.addPageOrSubcategory')" aria-haspopup="menu" :aria-expanded="menuOpenForId === `s-${category.id}` && menuMode === 'add'" @click.stop="openMenu($event, { kind: 'section', section: category, siblings: store.tree, parentId: null }, 'add')">
                <Plus class="w-3.5 h-3.5" aria-hidden="true" />
              </button>
              <button type="button" class="row-icon-btn" :aria-label="t('nav.actionsFor', { name: category.name })" :aria-expanded="menuOpenForId === `s-${category.id}`" aria-haspopup="menu" @click.stop="openMenu($event, { kind: 'section', section: category, siblings: store.tree, parentId: null })">
                <MoreHorizontal class="w-3.5 h-3.5" aria-hidden="true" />
              </button>
            </span>
          </div>

          <!-- Inhoud van de categorie -->
          <ul v-if="expanded.has(category.id)" role="list" class="nav-children">
            <!-- Subcategorieën -->
            <template v-for="(sub, subIndex) in category.children" :key="`sub-${sub.id}`">
              <li>
                <div
                  class="nav-row group"
                  :class="{
                    'opacity-60': !sub.isVisible && ui.canEdit,
                    'menu-open': menuOpenForId === `s-${sub.id}`,
                    'dragging': dnd.isDragged(`sub-${category.id}`, subIndex),
                    'drop-before': dnd.lineBefore(`sub-${category.id}`, subIndex),
                    'drop-after': dnd.lineAfterLast(`sub-${category.id}`, subIndex, category.children?.length ?? 0),
                  }"
                  :draggable="ui.canEdit ? 'true' : undefined"
                  @dragstart="dnd.start($event, `sub-${category.id}`, subIndex)"
                  @dragover="dnd.over($event, `sub-${category.id}`, subIndex)"
                  @drop.prevent="dropSections(category.children ?? [], category.id)"
                  @dragend="dnd.reset()"
                >
                  <button type="button" class="row-main" :aria-expanded="expanded.has(sub.id)" @click="toggleExpanded(sub.id)">
                    <ChevronRight class="w-3.5 h-3.5 text-gray-400 transition-transform flex-shrink-0" :class="{ 'rotate-90': expanded.has(sub.id) }" aria-hidden="true" />
                    <Folder class="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                    <input
                      v-if="renaming?.kind === 'section' && renaming.id === sub.id"
                      ref="inputEl"
                      v-model="inputValue"
                      class="inline-input"
                      :aria-label="t('nav.newNameLabel')"
                      @keydown.enter.prevent="commitInput"
                      @blur="commitInput"
                      @click.stop
                    >
                    <span v-else class="row-name font-medium">{{ sub.name }}</span>
                    <EyeOff v-if="!sub.isVisible && ui.canEdit" class="w-3.5 h-3.5 text-amber-600 flex-shrink-0" aria-hidden="true" />
                    <Lock
                      v-if="ui.canEdit ? hasOwnLock(sub) : sub.isLocked"
                      class="w-3.5 h-3.5 text-gray-400 flex-shrink-0"
                      :aria-label="t('access.category.lockedLabel')"
                    />
                  </button>
                  <span v-if="ui.canEdit" class="row-actions">
                    <button type="button" class="row-icon-btn row-icon-btn--add" :aria-label="t('nav.addPageToLabel', { name: sub.name })" :title="t('nav.addPageTitle')" @click.stop="startAdd(sub.id, 'page')">
                      <Plus class="w-3.5 h-3.5" aria-hidden="true" />
                    </button>
                    <button type="button" class="row-icon-btn" :aria-label="t('nav.actionsFor', { name: sub.name })" :aria-expanded="menuOpenForId === `s-${sub.id}`" aria-haspopup="menu" @click.stop="openMenu($event, { kind: 'section', section: sub, siblings: category.children ?? [], parentId: category.id })">
                      <MoreHorizontal class="w-3.5 h-3.5" aria-hidden="true" />
                    </button>
                  </span>
                </div>

                <!-- Pagina's binnen subcategorie -->
                <ul v-if="expanded.has(sub.id)" role="list" class="nav-children">
                  <li v-for="(page, pageIndex) in sub.pages" :key="`p-${page.id}`">
                    <div
                      class="nav-row group"
                      :class="{
                        'menu-open': menuOpenForId === `p-${page.id}`,
                        'dragging': dnd.isDragged(`psub-${sub.id}`, pageIndex),
                        'drop-before': dnd.lineBefore(`psub-${sub.id}`, pageIndex),
                        'drop-after': dnd.lineAfterLast(`psub-${sub.id}`, pageIndex, sub.pages?.length ?? 0),
                      }"
                      :draggable="ui.canEdit ? 'true' : undefined"
                      @dragstart="dnd.start($event, `psub-${sub.id}`, pageIndex)"
                      @dragover="dnd.over($event, `psub-${sub.id}`, pageIndex)"
                      @drop.prevent="dropPages(sub.pages ?? [], sub.id)"
                      @dragend="dnd.reset()"
                    >
                      <RouterLink :to="pageLink(page, sub)" class="row-main" :draggable="false" :class="{ 'row-active': isActivePage(page, sub) }">
                        <span class="row-lead" aria-hidden="true"></span>
                        <FileText class="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                        <input
                          v-if="renaming?.kind === 'page' && renaming.id === page.id"
                          ref="inputEl"
                          v-model="inputValue"
                          class="inline-input"
                          :aria-label="t('nav.newTitleLabel')"
                          @keydown.enter.prevent="commitInput"
                          @blur="commitInput"
                          @click.prevent.stop
                        >
                        <span v-else class="row-name">{{ page.title }}</span>
                        <span v-if="page.status === 'draft' && ui.canEdit" class="draft-badge" role="img" :aria-label="t('nav.draft')" :title="t('nav.draft')">
                  <Clock class="w-2.5 h-2.5" aria-hidden="true" />
                </span>
                        <EyeOff v-if="!page.isVisible && ui.canEdit" class="w-3.5 h-3.5 text-amber-600 flex-shrink-0" aria-hidden="true" />
                      </RouterLink>
                      <span v-if="ui.canEdit" class="row-actions">
                        <button type="button" class="row-icon-btn" :aria-label="t('nav.actionsFor', { name: page.title })" :aria-expanded="menuOpenForId === `p-${page.id}`" aria-haspopup="menu" @click.stop="openMenu($event, { kind: 'page', page, siblings: sub.pages ?? [], sectionId: sub.id })">
                          <MoreHorizontal class="w-3.5 h-3.5" aria-hidden="true" />
                        </button>
                      </span>
                    </div>

                    <!-- Tabs (kindpagina's) van deze pagina -->
                    <ul v-if="page.children?.length" role="list" class="nav-children">
                      <li v-for="(tab, tabIndex) in page.children" :key="`t-${tab.id}`">
                        <div
                          class="nav-row group"
                          :class="{
                            'menu-open': menuOpenForId === `p-${tab.id}`,
                            'dragging': dnd.isDragged(`tabs-${page.id}`, tabIndex),
                            'drop-before': dnd.lineBefore(`tabs-${page.id}`, tabIndex),
                            'drop-after': dnd.lineAfterLast(`tabs-${page.id}`, tabIndex, page.children?.length ?? 0),
                          }"
                          :draggable="ui.canEdit ? 'true' : undefined"
                          @dragstart="dnd.start($event, `tabs-${page.id}`, tabIndex)"
                          @dragover="dnd.over($event, `tabs-${page.id}`, tabIndex)"
                          @drop.prevent="dropPages(page.children ?? [], sub.id)"
                          @dragend="dnd.reset()"
                        >
                          <RouterLink :to="pageLink(tab, sub, page)" class="row-main" :draggable="false" :class="{ 'row-active': isActivePage(tab, sub, page) }">
                            <span class="row-lead" aria-hidden="true"></span>
                            <FileText class="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                            <input
                              v-if="renaming?.kind === 'page' && renaming.id === tab.id"
                              ref="inputEl"
                              v-model="inputValue"
                              class="inline-input"
                              :aria-label="t('nav.newTitleLabel')"
                              @keydown.enter.prevent="commitInput"
                              @blur="commitInput"
                              @click.prevent.stop
                            >
                            <span v-else class="row-name">{{ tab.title }}</span>
                            <span v-if="tab.status === 'draft' && ui.canEdit" class="draft-badge" role="img" :aria-label="t('nav.draft')" :title="t('nav.draft')">
                              <Clock class="w-2.5 h-2.5" aria-hidden="true" />
                            </span>
                            <EyeOff v-if="!tab.isVisible && ui.canEdit" class="w-3.5 h-3.5 text-amber-600 flex-shrink-0" aria-hidden="true" />
                          </RouterLink>
                          <span v-if="ui.canEdit" class="row-actions">
                            <button type="button" class="row-icon-btn" :aria-label="t('nav.actionsFor', { name: tab.title })" :aria-expanded="menuOpenForId === `p-${tab.id}`" aria-haspopup="menu" @click.stop="openMenu($event, { kind: 'page', page: tab, siblings: page.children ?? [], sectionId: sub.id, parentId: page.id })">
                              <MoreHorizontal class="w-3.5 h-3.5" aria-hidden="true" />
                            </button>
                          </span>
                        </div>
                      </li>
                    </ul>
                  </li>
                  <li v-if="adding && adding.parentId === sub.id">
                    <div class="nav-row">
                      <span class="row-main">
                        <span class="row-lead"></span>
                        <FileText class="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                        <input ref="inputEl" v-model="inputValue" class="inline-input" :placeholder="t('nav.newPagePlaceholder')" :aria-label="t('nav.newPageLabel')" @keydown.enter.prevent="commitInput" @blur="commitInput">
                      </span>
                    </div>
                  </li>
                  <!-- Onderaan altijd de knop zelf; bij een lege
                       subcategorie met de uitleg erboven. -->
                  <li v-else-if="ui.canEdit">
                    <p v-if="!sub.pages?.length" class="empty-category-hint">{{ t('nav.emptyCategoryHint') }}</p>
                    <button type="button" class="add-page-btn" @click="startAdd(sub.id, 'page')">
                      <Plus class="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                      {{ t('nav.newPage') }}
                    </button>
                  </li>
                </ul>
              </li>
            </template>

            <!-- Pagina's direct in de categorie -->
            <li v-for="(page, pageIndex) in category.pages" :key="`p-${page.id}`">
              <div
                class="nav-row group"
                :class="{
                  'menu-open': menuOpenForId === `p-${page.id}`,
                  'dragging': dnd.isDragged(`pcat-${category.id}`, pageIndex),
                  'drop-before': dnd.lineBefore(`pcat-${category.id}`, pageIndex),
                  'drop-after': dnd.lineAfterLast(`pcat-${category.id}`, pageIndex, category.pages?.length ?? 0),
                }"
                :draggable="ui.canEdit ? 'true' : undefined"
                @dragstart="dnd.start($event, `pcat-${category.id}`, pageIndex)"
                @dragover="dnd.over($event, `pcat-${category.id}`, pageIndex)"
                @drop.prevent="dropPages(category.pages ?? [], category.id)"
                @dragend="dnd.reset()"
              >
                <RouterLink :to="pageLink(page, category)" class="row-main" :draggable="false" :class="{ 'row-active': isActivePage(page, category) }">
                  <span class="row-lead" aria-hidden="true"></span>
                  <FileText class="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                  <input
                    v-if="renaming?.kind === 'page' && renaming.id === page.id"
                    ref="inputEl"
                    v-model="inputValue"
                    class="inline-input"
                    :aria-label="t('nav.newTitleLabel')"
                    @keydown.enter.prevent="commitInput"
                    @blur="commitInput"
                    @click.prevent.stop
                  >
                  <span v-else class="row-name">{{ page.title }}</span>
                  <span v-if="page.status === 'draft' && ui.canEdit" class="draft-badge" role="img" :aria-label="t('nav.draft')" :title="t('nav.draft')">
                  <Clock class="w-2.5 h-2.5" aria-hidden="true" />
                </span>
                  <EyeOff v-if="!page.isVisible && ui.canEdit" class="w-3.5 h-3.5 text-amber-600 flex-shrink-0" aria-hidden="true" />
                </RouterLink>
                <span v-if="ui.canEdit" class="row-actions">
                  <button type="button" class="row-icon-btn" :aria-label="t('nav.actionsFor', { name: page.title })" :aria-expanded="menuOpenForId === `p-${page.id}`" aria-haspopup="menu" @click.stop="openMenu($event, { kind: 'page', page, siblings: category.pages ?? [], sectionId: category.id })">
                    <MoreHorizontal class="w-3.5 h-3.5" aria-hidden="true" />
                  </button>
                </span>
              </div>

              <!-- Tabs (kindpagina's) van deze pagina -->
              <ul v-if="page.children?.length" role="list" class="nav-children">
                <li v-for="(tab, tabIndex) in page.children" :key="`t-${tab.id}`">
                  <div
                    class="nav-row group"
                    :class="{
                      'menu-open': menuOpenForId === `p-${tab.id}`,
                      'dragging': dnd.isDragged(`tabs-${page.id}`, tabIndex),
                      'drop-before': dnd.lineBefore(`tabs-${page.id}`, tabIndex),
                      'drop-after': dnd.lineAfterLast(`tabs-${page.id}`, tabIndex, page.children?.length ?? 0),
                    }"
                    :draggable="ui.canEdit ? 'true' : undefined"
                    @dragstart="dnd.start($event, `tabs-${page.id}`, tabIndex)"
                    @dragover="dnd.over($event, `tabs-${page.id}`, tabIndex)"
                    @drop.prevent="dropPages(page.children ?? [], category.id)"
                    @dragend="dnd.reset()"
                  >
                    <RouterLink :to="pageLink(tab, category, page)" class="row-main" :draggable="false" :class="{ 'row-active': isActivePage(tab, category, page) }">
                      <span class="row-lead" aria-hidden="true"></span>
                      <FileText class="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                      <input
                        v-if="renaming?.kind === 'page' && renaming.id === tab.id"
                        ref="inputEl"
                        v-model="inputValue"
                        class="inline-input"
                        :aria-label="t('nav.newTitleLabel')"
                        @keydown.enter.prevent="commitInput"
                        @blur="commitInput"
                        @click.prevent.stop
                      >
                      <span v-else class="row-name">{{ tab.title }}</span>
                      <span v-if="tab.status === 'draft' && ui.canEdit" class="draft-badge" role="img" :aria-label="t('nav.draft')" :title="t('nav.draft')">
                        <Clock class="w-2.5 h-2.5" aria-hidden="true" />
                      </span>
                      <EyeOff v-if="!tab.isVisible && ui.canEdit" class="w-3.5 h-3.5 text-amber-600 flex-shrink-0" aria-hidden="true" />
                    </RouterLink>
                    <span v-if="ui.canEdit" class="row-actions">
                      <button type="button" class="row-icon-btn" :aria-label="t('nav.actionsFor', { name: tab.title })" :aria-expanded="menuOpenForId === `p-${tab.id}`" aria-haspopup="menu" @click.stop="openMenu($event, { kind: 'page', page: tab, siblings: page.children ?? [], sectionId: category.id, parentId: page.id })">
                        <MoreHorizontal class="w-3.5 h-3.5" aria-hidden="true" />
                      </button>
                    </span>
                  </div>
                </li>
              </ul>
            </li>

            <!-- Inline toevoegen in deze categorie -->
            <li v-if="adding && adding.parentId === category.id">
              <div class="nav-row">
                <span class="row-main">
                  <span class="row-lead"></span>
                  <component :is="adding.kind === 'category' ? Folder : FileText" class="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                  <input
                    ref="inputEl"
                    v-model="inputValue"
                    class="inline-input"
                    :placeholder="adding.kind === 'category' ? t('nav.newSubcategoryPlaceholder') : t('nav.newPagePlaceholder')"
                    :aria-label="adding.kind === 'category' ? t('nav.newSubcategoryLabel') : t('nav.newPageLabel')"
                    @keydown.enter.prevent="commitInput"
                    @blur="commitInput"
                  >
                </span>
              </div>
            </li>

            <!-- Onderaan elke categorie een zichtbare knop, niet alleen bij
                 een lege: de +-knop op de rij erboven werd door nieuwe
                 gebruikers niet als "hier maak je een pagina" gelezen. Is de
                 categorie leeg, dan staat de uitleg erboven. -->
            <li v-else-if="ui.canEdit">
              <p v-if="!category.children?.length && !category.pages?.length" class="empty-category-hint">{{ t('nav.emptyCategoryHint') }}</p>
              <button type="button" class="add-page-btn" @click="startAdd(category.id, 'page')">
                <Plus class="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                {{ t('nav.newPage') }}
              </button>
            </li>
          </ul>
        </li>
      </template>

    </ul>
    <!-- Nieuwe hoofdcategorieën maak je aan via de "+" in de header. -->

    <!-- Flyout-menu -->
    <Teleport to="body">
      <transition name="menu-pop">
        <div
          v-if="target"
          ref="menuEl"
          role="menu"
          :aria-label="t('nav.actionsFor', { name: targetName })"
          class="fixed z-50 min-w-[220px] overflow-y-auto rounded-lg bg-white p-1.5 shadow-lg ring-1 ring-black ring-opacity-5 text-sm"
          :style="{ top: `${menuPos.top}px`, left: `${menuPos.left}px`, maxHeight: `${menuPos.maxHeight}px` }"
        >
          <!-- Add-modus (+-knop): alleen de toevoeg-acties. -->
          <template v-if="menuMode === 'add' && target.kind === 'section'">
            <button type="button" role="menuitem" class="menu-item" @click="startAdd(target.section.id, 'page')">
              <Plus class="w-4 h-4 text-gray-400" aria-hidden="true" /> {{ t('nav.newPage') }}
            </button>
            <button v-if="target.section.parentId === null" type="button" role="menuitem" class="menu-item" @click="startAdd(target.section.id, 'category')">
              <Plus class="w-4 h-4 text-gray-400" aria-hidden="true" /> {{ t('nav.newSubcategory') }}
            </button>
          </template>

          <template v-else>
          <button type="button" role="menuitem" class="menu-item" @click="startRename(target.kind === 'section' ? 'section' : 'page', target.kind === 'section' ? target.section.id : target.page.id, targetName)">
            <Pencil class="w-4 h-4 text-gray-400" aria-hidden="true" /> {{ t('nav.rename') }}
          </button>

          <button v-if="target.kind === 'page'" type="button" role="menuitem" class="menu-item" @click="duplicatePage">
            <Copy class="w-4 h-4 text-gray-400" aria-hidden="true" /> {{ t('nav.duplicate') }}
          </button>

          <template v-if="target.kind === 'section'">
            <button type="button" role="menuitem" class="menu-item" @click="startAdd(target.section.id, 'page')">
              <Plus class="w-4 h-4 text-gray-400" aria-hidden="true" /> {{ t('nav.newPage') }}
            </button>
            <button v-if="target.section.parentId === null" type="button" role="menuitem" class="menu-item" @click="startAdd(target.section.id, 'category')">
              <Plus class="w-4 h-4 text-gray-400" aria-hidden="true" /> {{ t('nav.newSubcategory') }}
            </button>
          </template>

          <div class="my-1 h-px bg-gray-100" role="separator"></div>

          <button type="button" role="menuitem" class="menu-item" @click="toggleVisibility">
            <component :is="targetVisible ? EyeOff : Eye" class="w-4 h-4 text-gray-400" aria-hidden="true" />
            {{ targetVisible ? t('nav.hideFromVisitors') : t('nav.makeVisible') }}
          </button>

          <!-- Alleen op categorieën: pagina's erven het slot van hun categorie
               (per pagina zou de site onnavigeerbaar worden). -->
          <button v-if="target.kind === 'section'" type="button" role="menuitem" class="menu-item" @click="openAccessDialog">
            <Lock class="w-4 h-4 text-gray-400" aria-hidden="true" />
            {{ t('nav.access') }}
            <span v-if="hasOwnLock(target.section)" class="ml-auto text-xs text-gray-400">
              {{ t('access.category.onBadge') }}
            </span>
          </button>

          <div class="my-1 h-px bg-gray-100" role="separator"></div>

          <button type="button" role="menuitem" class="menu-item" :disabled="targetIndex <= 0" @click="moveBy(-1)">
            <ArrowUp class="w-4 h-4 text-gray-400" aria-hidden="true" /> {{ t('nav.moveUp') }}
          </button>
          <button type="button" role="menuitem" class="menu-item" :disabled="targetIndex >= (target.siblings.length - 1)" @click="moveBy(1)">
            <ArrowDown class="w-4 h-4 text-gray-400" aria-hidden="true" /> {{ t('nav.moveDown') }}
          </button>
          <!-- Tabs horen bij hun ouderpagina; "verplaats naar" geldt niet. -->
          <button v-if="!isTabTarget" type="button" role="menuitem" class="menu-item" :aria-expanded="showMoveTargets" @click="toggleMoveTargets">
            <FolderInput class="w-4 h-4 text-gray-400" aria-hidden="true" /> {{ t('nav.moveTo') }}
            <ChevronRight class="ml-auto w-3.5 h-3.5 text-gray-400 transition-transform" :class="{ 'rotate-90': showMoveTargets }" aria-hidden="true" />
          </button>
          <div v-if="showMoveTargets && !isTabTarget" class="pl-7 py-0.5">
            <button
              v-for="destination in moveTargets"
              :key="destination.id ?? 'root'"
              type="button"
              role="menuitem"
              class="menu-item text-[13px]"
              @click="moveTo(destination.id)"
            >
              {{ destination.name }}
            </button>
            <p v-if="moveTargets.length === 0" class="px-2 py-1 text-xs text-gray-400">{{ t('nav.noOtherLocations') }}</p>
          </div>

          <div class="my-1 h-px bg-gray-100" role="separator"></div>

          <button type="button" role="menuitem" class="menu-item text-red-700 hover:bg-red-50" @click.stop="confirmDelete">
            <Trash2 class="w-4 h-4 text-red-600" aria-hidden="true" />
            {{ target.kind === 'section' ? t('nav.deleteWithContent') : t('nav.delete') }}
          </button>
          </template>
        </div>
      </transition>
    </Teleport>

    <CategoryAccessDialog
      :section="accessDialogSection"
      @close="accessDialogSection = null"
    />
  </div>
</template>

<style scoped>
/* Compacte, px-gebaseerde maatvoering: onafhankelijk van de (op brede
   schermen tot 20px opgeblazen) root font-size, zodat de boom strak blijft. */
.nav-tree {
  font-size: 13.5px;
  line-height: 1.35;
}
/* Nette, kleine ritmische afstand tussen alle rijen, op elk niveau. */
.nav-tree li + li {
  margin-top: 1px;
}
/* Inspringing per niveau: 17px. */
.nav-children {
  margin-left: 8px;
  padding-left: 9px;
}

.nav-row {
  display: flex;
  align-items: center;
  border-radius: 6px;
  position: relative;
}
.nav-row:hover,
.nav-row.menu-open {
  background: #ffffff;
}
/* Slepen: gesleepte rij vervaagt, invoegpositie krijgt een lijn. */
.nav-row.dragging {
  opacity: 0.4;
}
.nav-row[draggable='true'] {
  cursor: grab;
}
.nav-row.drop-before::before,
.nav-row.drop-after::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background: theme('colors.primary.500');
  border-radius: 1px;
  z-index: 1;
}
.nav-row.drop-before::before {
  top: -1px;
}
.nav-row.drop-after::after {
  bottom: -1px;
}
.row-main {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  /* De globale WCAG-touch-target-regel (main.css) forceert min-height 24px
     op elke <a>/<button>. Nav-rijen zijn full-width klikbaar, dus de
     hoogte-eis van 2.5.8 is hier niet kritiek; de hoogte wordt nu door de
     padding bepaald (~23px). */
  min-height: 0;
  text-align: left;
  font-size: 13.5px;
  color: theme('colors.gray.700');
  border-radius: 6px;
  /* Pagina-rijen zijn RouterLinks (<a>); de globale a-underline hoort
     niet thuis in de navigatieboom. */
  text-decoration: none;
}
/* Iconen in de rij (chevron, folder, file, oog) uniform en compact,
   los van de opgeblazen root font-size. */
.row-main svg {
  width: 15px;
  height: 15px;
}
/* Lege plek links van een pagina-rij, uitgelijnd onder de chevron-kolom. */
.row-lead {
  width: 15px;
  flex-shrink: 0;
}
.row-main.row-active {
  color: theme('colors.primary.700');
  background: #ffffff;
}
.row-name {
  flex: 1;
  min-width: 0;
  max-width: 200px;
  overflow-wrap: break-word;
  white-space: normal;
}
/* Gedimd zichtbaar in plaats van verborgen tot hover: in gebruikerstests
   werden de +- en ⋯-knoppen anders simpelweg niet gevonden. De dimming zit
   op de knop en niet op deze rij, want opacity van een ouder is door een
   kind niet terug te draaien - en het plusje moet er juist buiten vallen. */
.row-actions {
  display: flex;
  align-items: center;
  gap: 1px;
  padding-right: 2px;
  flex-shrink: 0;
}
.row-actions svg {
  width: 15px;
  height: 15px;
}
.row-icon-btn {
  opacity: 0.55;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  /* De globale WCAG-touch-target-regel (main.css) is inmiddels zelf op
     24x24px gezet (WCAG 2.2 AA, 2.5.8) — deze expliciete waarde staat hier
     vooral ter documentatie van de bewuste keuze voor deze compacte rij. */
  min-width: 24px;
  min-height: 24px;
  border-radius: 5px;
  color: theme('colors.gray.500');
}
.nav-row:hover .row-icon-btn,
.nav-row.menu-open .row-icon-btn,
.nav-row:focus-within .row-icon-btn {
  opacity: 1;
}
.row-icon-btn:hover {
  background: theme('colors.gray.200');
  color: theme('colors.gray.800');
}
/* Het plusje is de belangrijkste actie op de rij en dimt daarom niet mee:
   grijs op grijs werd het aangezien voor een uitgeschakelde knop. */
.row-icon-btn--add {
  opacity: 1;
  color: theme('colors.gray.700');
  background: theme('colors.gray.100');
}
.row-icon-btn--add:hover {
  background: theme('colors.primary.100');
  color: theme('colors.primary.700');
}
.inline-input {
  flex: 1;
  min-width: 0;
  font-size: 13.5px;
  border: 1px solid theme('colors.primary.500');
  border-radius: 5px;
  padding: 1px 6px;
  background: white;
  color: theme('colors.gray.900');
}
/* Lege categorie: korte uitleg + eerste-pagina-knop. */
.empty-category-hint {
  padding: 2px 8px 0 30px;
  font-size: 12px;
  font-style: italic;
  color: theme('colors.gray.500');
}
/* "Nieuwe pagina" onder elke categorie - bewust dezelfde gestippelde knop
   als "Nieuwe categorie" in de header, zodat "hier maak je iets nieuws"
   er door de hele app hetzelfde uitziet. */
.add-page-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 4px 0 2px 2px;
  padding: 3px 10px;
  border: 1px dashed theme('colors.gray.300');
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: theme('colors.gray.600');
}
.add-page-btn:hover {
  color: theme('colors.primary.700');
  border-color: theme('colors.primary.400');
  background: theme('colors.primary.50');
}
.add-page-btn svg {
  width: 15px;
  height: 15px;
}
.draft-badge {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  background: theme('colors.amber.100');
  color: theme('colors.amber.600');
}
</style>
